import Flatbush from 'flatbush'
import { deserialize } from 'flatgeobuf/lib/mjs/geojson'
import GeoJSON from 'geojson'
import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { NotamFeature, NotamFeatureCollection } from '@/domain/models/Notam'
import { Boundaries } from '@/domain/protocols/Boundaries'
import { Result } from '@/domain/protocols/Result'

import { INotamRepository } from './INotamRepository'

import { DatasetCache } from '@/infra/datasetCache/DatasetCache'
import { loadDatasetBytes } from '@/infra/datasetCache/loadDatasetBytes'
import { describeHttpFailure } from '@/infra/http/httpFailure'
import type { IHttpClient } from '@/infra/http/IHttpClient'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import type { ILogger } from '@/infra/logger/ILogger'

interface RawNotamProperties {
  identifier: string
  type: string
  fir: string
  code: string
  purpose: string
  scope: string
  traffic: string
  valid_from: string
  valid_until: string
  active_periods?: string
  locality: string
  locality_type: string
  text: string
  text_en?: string
  language: string
  affected_services_category: string
  lower_limit: number
  lower_limit_reference: string
  lower_limit_text?: string
  upper_limit: number
  upper_limit_reference: string
  upper_limit_text?: string
  dispatched_at: string
  origin: string
  region: string
  replaces_notam_identifier?: string
  bbox_minx?: number
  bbox_miny?: number
  bbox_maxx?: number
  bbox_maxy?: number
}

interface RawNotamFeature {
  type: 'Feature'
  geometry: GeoJSON.MultiPolygon
  properties: RawNotamProperties
}

@injectable()
export class NotamRepository implements INotamRepository {
  @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient
  @inject(InjectionTokens.Logger) private logger: ILogger
  @inject(InjectionTokens.DatasetCache) private datasetCache: DatasetCache

  private notams: NotamFeatureCollection = { type: 'FeatureCollection', features: [] }
  private spatialIndex: Flatbush | null = null
  private localityIndex: Map<string, NotamFeature[]> = new Map()
  private localityTypeIndex: Map<string, NotamFeature[]> = new Map()
  private idIndex: Map<string, NotamFeature> = new Map()
  private lastLoadedAt: Date | null = null

  private updateTime = 10000
  private updateSum = 15000

  private static readonly AUTO_UPDATE_INTERVAL = 10 * 60 * 1000
  private autoUpdateProcess: ReturnType<typeof setTimeout> | null = null
  private onlineListener: (() => void) | null = null

  protected getRemoteUrl(): string {
    return `${import.meta.env.VITE_JETSTREAM_CDN_BASEURL}/bra/notams_upgraded.fgb`
  }

  async init(): Promise<Result<void>> {
    const success = await this._tryUpdateFromRemote()

    if (!success) {
      return Result.fail('Could not load notams')
    }

    return Result.ok()
  }

  private async _loadNotams(arrayBuffer: ArrayBuffer): Promise<boolean> {
    try {
      const uint8Array = new Uint8Array(arrayBuffer)
      const features = deserialize(uint8Array)

      const notamFeatures: NotamFeature[] = []
      for await (const feature of features) {
        const rawFeature = feature as RawNotamFeature

        if (rawFeature.properties) {
          const { bbox_minx, bbox_miny, bbox_maxx, bbox_maxy } = rawFeature.properties

          const mappedProperties = {
            identifier: rawFeature.properties.identifier,
            type: rawFeature.properties.type,
            fir: rawFeature.properties.fir,
            code: rawFeature.properties.code,
            purpose: rawFeature.properties.purpose,
            scope: rawFeature.properties.scope,
            traffic: rawFeature.properties.traffic,
            validFrom: rawFeature.properties.valid_from,
            validUntil: rawFeature.properties.valid_until,
            activePeriods: rawFeature.properties.active_periods,
            locality: rawFeature.properties.locality,
            localityType: rawFeature.properties.locality_type,
            text: rawFeature.properties.text,
            textEn: rawFeature.properties.text_en,
            language: rawFeature.properties.language,
            affectedServicesCategory: rawFeature.properties.affected_services_category,
            lowerLimit: rawFeature.properties.lower_limit,
            lowerLimitReference: rawFeature.properties.lower_limit_reference,
            lowerLimitText: rawFeature.properties.lower_limit_text,
            upperLimit: rawFeature.properties.upper_limit,
            upperLimitReference: rawFeature.properties.upper_limit_reference,
            upperLimitText: rawFeature.properties.upper_limit_text,
            dispatchedAt: rawFeature.properties.dispatched_at,
            origin: rawFeature.properties.origin,
            region: rawFeature.properties.region,
            replacesNotamIdentifier: rawFeature.properties.replaces_notam_identifier
          }

          const typedFeature: NotamFeature = {
            ...rawFeature,
            properties: mappedProperties
          }

          if (
            bbox_minx !== undefined &&
            bbox_miny !== undefined &&
            bbox_maxx !== undefined &&
            bbox_maxy !== undefined
          ) {
            typedFeature.bbox = [bbox_minx, bbox_miny, bbox_maxx, bbox_maxy]
          }

          notamFeatures.push(typedFeature)
        }
      }

      this.notams = {
        type: 'FeatureCollection',
        features: notamFeatures
      }

      this._buildAllIndexes()
      this.lastLoadedAt = new Date()

      return true
    } catch (e: any) {
      this.logger.error('JetStreamNotamRepository', `Could not load notams: ${e.message}`)
      return false
    }
  }

  private async _tryUpdateFromRemote(): Promise<boolean> {
    this.logger.info('NotamRepository', 'Updating from remote')
    const result = await this._updateFromRemote()

    if (result.isFailure) {
      this.logger.error(
        'NotamRepository',
        `Error updating from remote, trying again in ${this.updateTime}ms: ${result.error}`
      )
      setTimeout(() => this._tryUpdateFromRemote(), this.updateTime)
      this.updateTime = Math.min(this.updateTime + this.updateSum, 5 * 60 * 1000)

      return false
    } else {
      this.logger.info('NotamRepository', 'Updated from remote')
      return true
    }
  }

  private async _updateFromRemote(): Promise<Result<void>> {
    try {
      // Cache-first, com o TTL mais curto de todos os datasets (15 min): NOTAM e o dado mais
      // pereciel. O auto-update de 10 min desta classe continua valendo por cima disto.
      const loaded = await loadDatasetBytes(
        this.httpClient,
        this.datasetCache,
        'notams',
        this.getRemoteUrl(),
        this.logger
      )

      if (!loaded) {
        return Result.fail('Could not fetch notam dataset')
      }

      const loadResult = await this._loadNotams(loaded.bytes.buffer as ArrayBuffer)

      if (!loadResult) {
        return Result.fail('Could not load notams after download')
      }

      return Result.ok()
    } catch (error: any) {
      this.logger.error('JetStreamNotamRepository', `_updateFromRemote: ${error.message}`)
      return Result.fail(error)
    }
  }

  private _buildAllIndexes(): void {
    const features = this.notams.features

    this.localityIndex.clear()
    this.localityTypeIndex.clear()
    this.idIndex.clear()

    if (features.length === 0) {
      this.spatialIndex = null
      return
    }

    this.spatialIndex = new Flatbush(features.length)

    features.forEach((notam: NotamFeature) => {
      if (notam.bbox && notam.bbox.length === 4) {
        const [minX, minY, maxX, maxY] = notam.bbox
        this.spatialIndex!.add(minX, minY, maxX, maxY)
      }

      const locality = notam.properties.locality
      if (locality) {
        const icaos = locality.split(' ')
        icaos.forEach((icao: string) => {
          const trimmedIcao = icao.trim()
          if (trimmedIcao) {
            if (!this.localityIndex.has(trimmedIcao)) {
              this.localityIndex.set(trimmedIcao, [])
            }
            this.localityIndex.get(trimmedIcao)!.push(notam)
          }
        })
      }

      const type = notam.properties.localityType
      if (type) {
        if (!this.localityTypeIndex.has(type)) {
          this.localityTypeIndex.set(type, [])
        }
        this.localityTypeIndex.get(type)!.push(notam)
      }

      const id = notam.properties.identifier
      if (id) {
        this.idIndex.set(id, notam)
      }
    })

    this.spatialIndex.finish()
  }

  getAll(): NotamFeatureCollection {
    return this.notams
  }

  getByBoundingBox(bounds: Boundaries): NotamFeature[] {
    if (!this.spatialIndex) {
      return []
    }

    const indices = this.spatialIndex.search(
      bounds.southWest.longitude,
      bounds.southWest.latitude,
      bounds.northEast.longitude,
      bounds.northEast.latitude
    )

    return indices.map((index: number) => this.notams.features[index])
  }

  getByLocality(icao: string): NotamFeature[] {
    const baseIcao = icao.split('_')[0]
    return this.localityIndex.get(baseIcao) || []
  }

  getByLocalityType(type: 'ADHP' | 'FIR' | 'TMA'): NotamFeature[] {
    return this.localityTypeIndex.get(type) || []
  }

  getById(id: string): NotamFeature | null {
    return this.idIndex.get(id) || null
  }

  getLastLoadedAt(): Date | null {
    return this.lastLoadedAt
  }

  async refresh(): Promise<boolean> {
    return this._tryUpdateFromRemote()
  }

  private _scheduleNextAutoUpdate(): void {
    if (this.autoUpdateProcess) {
      clearTimeout(this.autoUpdateProcess)
    }

    this.autoUpdateProcess = setTimeout(async () => {
      this.logger.info('NotamRepository', 'Auto-update triggered')
      await this._tryUpdateFromRemote()
      this._scheduleNextAutoUpdate()
    }, NotamRepository.AUTO_UPDATE_INTERVAL)
  }

  async start(): Promise<Result<void>> {
    this.logger.info('NotamRepository', 'Starting service')

    this._scheduleNextAutoUpdate()
    this._attachOnlineListener()

    return Result.ok()
  }

  async stop(): Promise<Result<void>> {
    this.logger.info('NotamRepository', 'Stopping service')

    if (this.autoUpdateProcess) {
      clearTimeout(this.autoUpdateProcess)
      this.autoUpdateProcess = null
    }

    this._detachOnlineListener()

    return Result.ok()
  }

  private _attachOnlineListener(): void {
    if (typeof window === 'undefined' || this.onlineListener) {
      return
    }

    this.onlineListener = () => {
      if (!this.lastLoadedAt || Date.now() - this.lastLoadedAt.getTime() >= NotamRepository.AUTO_UPDATE_INTERVAL) {
        void this._tryUpdateFromRemote()
      }
    }

    window.addEventListener('online', this.onlineListener)
  }

  private _detachOnlineListener(): void {
    if (typeof window === 'undefined' || !this.onlineListener) {
      return
    }

    window.removeEventListener('online', this.onlineListener)
    this.onlineListener = null
  }
}
